import { Plus, Search, Pencil, Trash2, Eye, EyeOff, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import KeysForm from "./KeysForm";
import Pagination from "../../../../Component/Common/Pagination";
import RegularLoader from "../../../../Component/Loader/RegularLoader";
import { SectionCard } from "../../../../Component/AdminComponents/SectionCard";
import RefreshButton from "../../../../Component/Common/RefreshButton";
import { BASE_URL } from "../../../../config/api";
import axios from "axios";

export default function KeysTable({ refresh = false, onUpdate, onKeysCountChange }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);

  // ✅ Track which secret is visible (no hook inside map)
  const [visibleSecrets, setVisibleSecrets] = useState({});

  const pageSize = 7;

  /* ================= DEBOUNCE ================= */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  /* ================= FETCH KEYS ================= */
  const fetchKeys = async () => {

    setLoading(
      true
    );

    try {

      const response =
        await axios.get(
          `${BASE_URL}payment/keys`,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const data =
        response.data;

      const filteredData =
        debouncedSearch
          ? data.filter(
              (k) =>
                k.key_id
                  .toLowerCase()
                  .includes(
                    debouncedSearch.toLowerCase()
                  ) ||
                k.key_secret
                  .toLowerCase()
                  .includes(
                    debouncedSearch.toLowerCase()
                  )
            )
          : data;

      setKeys(
        filteredData
      );

      onKeysCountChange &&
        onKeysCountChange(
          filteredData.length
        );

    } catch (err) {

      console.error(
        err
      );

      toast.error(
        err?.response?.data
          ?.message ||
        err?.message
      );

    } finally {

      setLoading(
        false
      );

    }
  };

  useEffect(() => {
    fetchKeys();
  }, [refresh, debouncedSearch]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(keys.length / pageSize);
  const paginatedKeys = keys.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  /* ================= HELPERS ================= */
  const toggleSecret = (id) => {
    setVisibleSecrets((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const openEditModal = (key) => {
    setSelectedKey(key);
    setModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    setModalOpen(false);
    fetchKeys();
    onUpdate && onUpdate();
  };

  const handleDelete = async (
    key
  ) => {

    if (
      !window.confirm(
        `Delete key "${key.key_id}"?`
      )
    ) return;

    try {

      await axios.delete(
        `${BASE_URL}payment/keys/${key.id}`,
        {
          headers: {
            Accept:
              "application/json",

            "Content-Type":
              "application/json",
          },
        }
      );

      toast.success(
        "Key deleted successfully!"
      );

      fetchKeys();

      onUpdate &&
        onUpdate();

    } catch (err) {

      console.error(
        err
      );

      toast.error(
        err?.response?.data
          ?.message ||
        err?.message
      );

    }
  };

  /* ================= UI ================= */
  return (
    <SectionCard title="Keys Management">

      {/* Search + Refresh */}
      <div className="flex justify-end gap-2 items-center mb-3">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50">
          <Search className="w-4 h-4 text-slate-600" />
          <input
            type="text"
            placeholder="Search keys..."
            className="bg-transparent text-xs outline-none"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <RefreshButton onClick={fetchKeys} />
      </div>

      {/* Loader */}
      {loading && (
        <div className="flex justify-center items-center h-[30vh]">
          <RegularLoader />
        </div>
      )}

      {/* No Records */}
      {!loading && keys.length === 0 && (
        <p className="text-center text-sm text-slate-500 py-4">
          No keys found.
        </p>
      )}

      {/* ================= DATA VIEW ================= */}
      {!loading && keys.length > 0 && (
        <>
          {/* ================= MOBILE CARD VIEW ================= */}
          <div className="md:hidden space-y-4">
            {paginatedKeys.map((key) => (
              <div
                key={key.id}
                className="bg-white rounded-2xl border border-slate-200 border-l-4 border-l-blue-500 p-4 shadow-sm hover:shadow-md transition-all"
              >
                {/* Header */}
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">
                      Key #{key.id}
                    </p>

                    <p className="text-sm font-semibold text-slate-800 mt-1 break-all">
                      {key.key_id}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(key)}
                      className="h-8 w-8 rounded-lg flex items-center justify-center bg-amber-100 text-amber-600 hover:bg-amber-200 transition"
                    >
                      <Pencil size={16} />
                    </button>

                    <button
                      onClick={() => handleDelete(key)}
                      className="h-8 w-8 rounded-lg flex items-center justify-center bg-rose-100 text-rose-600 hover:bg-rose-200 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-slate-100 my-3" />

                {/* Secret */}
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">
                    Secret Key
                  </p>

                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="text-xs font-medium text-slate-900 break-all">
                      {visibleSecrets[key.id]
                        ? key.key_secret
                        : "•".repeat(Math.min(key.key_secret.length, 16))}
                    </p>

                    <div className="flex gap-3">
                      <button
                        onClick={() => toggleSecret(key.id)}
                        className="text-blue-600"
                      >
                        {visibleSecrets[key.id] ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>

                      <button
                        onClick={() => handleCopy(key.key_secret)}
                        className="text-slate-500"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ================= DESKTOP TABLE ================= */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full text-sm text-center">
              <thead>
                <tr className="border-b border-slate-100 text-slate-500">
                  <th className="py-2 px-2">ID</th>
                  <th className="py-2 px-2">Key ID</th>
                  <th className="py-2 px-2">Key Secret</th>
                  <th className="py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedKeys.map((key) => (
                  <tr
                    key={key.id}
                    className="border-b border-slate-50 hover:bg-blue-50/50"
                  >
                    <td className="py-2 px-2">{key.id}</td>
                    <td className="py-2 px-2">{key.key_id}</td>
                    <td className="py-2 px-2">{key.key_secret}</td>
                    <td className="py-2 px-2 flex justify-center gap-2">
                      <button
                        className="text-amber-600"
                        onClick={() => openEditModal(key)}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        className="text-red-600"
                        onClick={() => handleDelete(key)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center md:justify-end">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {modalOpen && selectedKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <KeysForm
            badge={selectedKey}
            onSuccess={handleUpdateSuccess}
            onClose={() => setModalOpen(false)}
          />
        </div>
      )}
    </SectionCard>
  );
}



// import { Plus, Search, Pencil, Trash2 } from "lucide-react";
// import { useEffect, useState } from "react";
// import toast from "react-hot-toast";
// import KeysForm from "./KeysForm";
// import Pagination from "../../../../Component/Common/Pagination";
// import RegularLoader from "../../../../Component/Loader/RegularLoader";
// import { SectionCard } from "../../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../../Component/Common/BlueButton";
// import RefreshButton from "../../../../Component/Common/RefreshButton";
// import { BASE_URL } from "../../../../config/api";

// export default function KeysTable({ refresh = false, onUpdate, onKeysCountChange }) {
//   const [keys, setKeys] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [search, setSearch] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");
//   const [modalOpen, setModalOpen] = useState(false);
//   const [selectedKey, setSelectedKey] = useState(null);

//   const pageSize = 7;

//   // Debounce search
//   useEffect(() => {
//     const timer = setTimeout(() => setDebouncedSearch(search), 400);
//     return () => clearTimeout(timer);
//   }, [search]);

//   const fetchKeys = async () => {
//     setLoading(true);
//     try {
//       const res = await fetch(`${BASE_URL}payment/keys`);
//       const data = await res.json();
//       if (!res.ok) throw new Error("Failed to fetch keys");

//       const filteredData = debouncedSearch
//         ? data.filter(k => 
//             k.key_id.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
//             k.key_secret.toLowerCase().includes(debouncedSearch.toLowerCase())
//           )
//         : data;

//       setKeys(filteredData);
//       onKeysCountChange && onKeysCountChange(filteredData.length);
//     } catch (err) {
//       toast.error(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchKeys();
//   }, [refresh, debouncedSearch]);

//   const totalPages = Math.ceil(keys.length / pageSize);
//   const paginatedKeys = keys.slice((currentPage - 1) * pageSize, currentPage * pageSize);

//   const openEditModal = (key) => {
//     setSelectedKey(key);
//     setModalOpen(true);
//   };

//   const handleUpdateSuccess = () => {
//     setModalOpen(false);
//     fetchKeys();
//     onUpdate && onUpdate();
//   };

//   const handleDelete = async (key) => {
//     if (!window.confirm(`Are you sure you want to delete key "${key.key_id}"?`)) return;

//     try {
//       const res = await fetch(`${BASE_URL}payment/keys/${key.id}`, {
//         method: "DELETE",
//       });
//       if (!res.ok) throw new Error("Failed to delete key");

//       toast.success("Key deleted successfully!");
//       fetchKeys();
//       onUpdate && onUpdate();
//     } catch (err) {
//       toast.error(err.message || "Something went wrong");
//     }
//   };

//   return (
//     <SectionCard title="Keys Management">
//       {/* Search + Refresh */}
//       <div className="flex justify-end gap-2 items-center mb-2">
//         <div className="flex items-center gap-1 px-3 py-1 rounded-xl border border-slate-200 bg-slate-50">
//           <Search className="w-4 h-4 text-slate-800" />
//           <input
//             type="text"
//             placeholder="Search keys..."
//             className="bg-transparent text-xs outline-none w-auto placeholder:text-slate-700"
//             value={search}
//             onChange={(e) => {
//               setSearch(e.target.value);
//               setCurrentPage(1);
//             }}
//           />
//         </div>
//         <RefreshButton onClick={fetchKeys} />
//       </div>

//       {/* Loader */}
//       {loading && (
//         <div className="flex justify-center items-center h-[30vh]">
//           <RegularLoader />
//         </div>
//       )}

//       {/* No Records */}
//       {!loading && keys.length === 0 && (
//         <p className="text-center text-sm text-slate-500 py-3">
//           No keys found.
//         </p>
//       )}

//       {/* Table */}
//       {/* ================= DATA VIEW ================= */}
//       {!loading && keys.length > 0 && (
//         <>
//           {/* ================= MOBILE CARD VIEW ================= */}
//           <div className="md:hidden space-y-4">
//             {paginatedKeys.map((key) => (
//               <div
//                 key={key.id}
//                 className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition"
//               >
//                 {/* Header */}
//                 <div className="flex justify-between items-center mb-3">
//                   <div>
//                     <p className="text-[11px] text-slate-400">ID</p>
//                     <p className="text-sm font-semibold text-slate-800">
//                       {key.id}
//                     </p>
//                   </div>

//                   <div className="flex gap-3">
//                     <button
//                       className="text-amber-600"
//                       onClick={() => openEditModal(key)}
//                     >
//                       <Pencil size={18} />
//                     </button>

//                     <button
//                       className="text-red-600"
//                       onClick={() => handleDelete(key)}
//                     >
//                       <Trash2 size={18} />
//                     </button>
//                   </div>
//                 </div>

//                 <div className="h-px bg-slate-100 mb-3" />

//                 {/* Key Info */}
//                 <div className="space-y-2 text-xs">
//                   <div>
//                     <span className="text-slate-400">Key ID</span>
//                     <p className="font-medium text-slate-900 break-all">
//                       {key.key_id}
//                     </p>
//                   </div>

//                   <div>
//                     <span className="text-slate-400">Key Secret</span>
//                     <p className="font-medium text-slate-900 break-all">
//                       {key.key_secret}
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>

//           {/* ================= DESKTOP TABLE ================= */}
//           <div className="hidden md:block overflow-x-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-blue-300">
//             <table className="min-w-full text-[10px] sm:text-sm text-center">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500">
//                   <th className="py-2 px-2">ID</th>
//                   <th className="py-2 px-2">Key ID</th>
//                   <th className="py-2 px-2">Key Secret</th>
//                   <th className="py-2 px-2">Actions</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {paginatedKeys.map((key) => (
//                   <tr
//                     key={key.id}
//                     className="border-b border-slate-50 hover:bg-blue-50/50"
//                   >
//                     <td className="py-2 px-2">{key.id}</td>
//                     <td className="py-2 px-2">{key.key_id}</td>
//                     <td className="py-2 px-2">{key.key_secret}</td>
//                     <td className="py-2 px-2 flex justify-center gap-2">
//                       <button
//                         className="text-amber-600 hover:text-amber-800"
//                         onClick={() => openEditModal(key)}
//                       >
//                         <Pencil size={16} />
//                       </button>
//                       <button
//                         className="text-red-600 hover:text-red-800"
//                         onClick={() => handleDelete(key)}
//                       >
//                         <Trash2 size={16} />
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>

//           {/* ================= PAGINATION ================= */}
//           {totalPages > 1 && (
//             <div className="mt-4 flex justify-center md:justify-end">
//               <Pagination
//                 currentPage={currentPage}
//                 totalPages={totalPages}
//                 onPageChange={setCurrentPage}
//               />
//             </div>
//           )}
//         </>
//       )}


//       {/* Edit Modal */}
//       {modalOpen && selectedKey && !loading && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <KeysForm
//             badge={selectedKey}
//             onSuccess={handleUpdateSuccess}
//             onClose={() => setModalOpen(false)}
//           />
//         </div>
//       )}
//     </SectionCard>
//   );
// }
